import React, { useState } from 'react';
import { AlertCircle, ChevronRight, FileText, Loader2, Trash2, X, CheckCircle2 } from 'lucide-react';
import { Meeting } from '../types';
import type { CalendarEventDTO } from '../global';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { CalendarCard } from '../features/calendar/CalendarCard';
import { BotStatusBadge } from '../features/bot/BotStatusBadge';
import { groupMeetings } from '../features/meetings/groupMeetings';
import { displaySummaryText } from '../features/recording/summaryRules';
import { meetingDragProps } from '../features/folders/dragMeeting';
import { plural, t } from '../lib/i18n';

interface HomeViewProps {
  meetings: Meeting[];
  onSelectMeeting: (meeting: Meeting) => void;
  isDarkMode: boolean;
  onDeleteMeetings: (ids: string[]) => void;
  /** Takvim etkinliği için yalnızca sistem sesiyle kayıt (bot gönderilmez). */
  onRecordSystemAudioForEvent: (event: CalendarEventDTO) => void;
  onError: (message: string) => void;
  isLoading: boolean;
  error: string | null;
}

export const HomeView: React.FC<HomeViewProps> = ({
  meetings,
  onSelectMeeting,
  isDarkMode,
  onDeleteMeetings,
  onRecordSystemAudioForEvent,
  onError,
  isLoading,
  error
}) => {
  const isOnline = useOnlineStatus();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelected = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    const confirmed = window.confirm(
      t(
        `${selectedIds.size} toplantı çöp kutusuna taşınacak. 30 gün içinde oradan geri alabilirsin, sonra kalıcı olarak silinir.`,
        `${plural(selectedIds.size, 'meeting', 'meetings')} will be moved to the trash. You can restore them within 30 days; after that they’re permanently deleted.`
      )
    );
    if (!confirmed) return;
    onDeleteMeetings(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const { recent: recentGroups, past: earlierMeetings } = groupMeetings(meetings);

  const renderRow = (meeting: Meeting, dateLabel: string, icon: React.ReactNode) => {
    const isSelected = selectedIds.has(meeting.id);
    return (
      <div
        key={meeting.id}
        {...meetingDragProps(meeting.id)}
        onClick={() => onSelectMeeting(meeting)}
        className={`flex items-center justify-between gap-3 -mx-2 px-2 py-3 rounded-lg transition-colors cursor-pointer group ${
          isDarkMode
            ? 'text-neutral-200 hover:bg-[#1F1F23]'
            : 'text-neutral-900 hover:bg-neutral-100'
        }`}
      >
        <div className="flex items-center gap-3.5 min-w-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleSelected(meeting.id);
            }}
            aria-label={isSelected ? t('Seçimi kaldır', 'Deselect') : t('Seç', 'Select')}
            /**
             * Eski hâlde seçim varken sınıf listesi hem `opacity-0` hem
             * `opacity-100` içeriyordu. İkisi de aynı özgüllükte olduğu için
             * kazananı sınıfların yazılış sırası değil Tailwind'in ürettiği
             * CSS'teki sıra belirliyordu — yani onay kutusu bazen görünmüyordu.
             * Artık her durum için tek bir opaklık sınıfı üretiliyor.
             */
            className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-all ${
              isSelected
                ? 'bg-[#3AC8E0] border-[#3AC8E0] opacity-100'
                : selectedIds.size > 0
                  ? 'border-neutral-500 opacity-100'
                  : 'border-neutral-500 opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
            }`}
          >
            {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-[#F0EEE6]" strokeWidth={2.5} />}
          </button>
          {icon}
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[14px] font-semibold truncate group-hover:text-[#46CBE2] transition-colors">
                {meeting.title}
              </span>
              {meeting.bot && <BotStatusBadge bot={meeting.bot} />}
            </div>
            <div className="text-[12px] text-neutral-500 truncate max-w-md hidden sm:block">
              {meeting.bot?.error || displaySummaryText(meeting.summary.tldr)}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3.5 shrink-0 ml-2">
          <span className="text-[12.5px] font-mono text-neutral-500 tabular-nums">
            {dateLabel}
          </span>
          <ChevronRight className="w-4 h-4 text-neutral-600 group-hover:translate-x-0.5 group-hover:text-neutral-400 transition-all" />
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-8 select-none">
      {/* "What's next" — takvim kartı kendi durumunu yönetir (features/calendar) */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h1 className={`text-[26px] font-bold tracking-tight ${
            isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'
          }`}>
            {t('Sırada ne var', 'What’s next')}
          </h1>
          {!isOnline && (
            <div className="flex items-center gap-2.5">
              <span className="text-[12.5px] text-rose-400 font-medium flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                {t('Çevrimdışı — takvim ve AI kullanılamıyor', 'Offline — calendar and AI are unavailable')}
              </span>
            </div>
          )}
        </div>

        <div data-tour="next-meeting">
          <CalendarCard
            isDarkMode={isDarkMode}
            onRecordSystemAudio={onRecordSystemAudioForEvent}
            onError={onError}
          />
        </div>
      </div>

      {/* Bulk-action bar — bir veya daha fazla toplantı seçiliyken görünür */}
      {selectedIds.size > 0 && (
        <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl border ${
          isDarkMode ? 'bg-[#19191C] border-[#3F3F42]' : 'bg-neutral-50 border-neutral-200'
        }`}>
          <span className="text-[13px] font-medium text-neutral-300">
            {t(`${selectedIds.size} seçili`, `${selectedIds.size} selected`)}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 text-[13px] font-semibold text-rose-400 hover:text-rose-300 px-2.5 py-1.5 rounded-lg hover:bg-rose-500/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('Sil', 'Delete')}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="flex items-center gap-1.5 text-[13px] font-medium text-neutral-400 hover:text-[#F0EEE6] px-2.5 py-1.5 rounded-lg hover:bg-neutral-700/30 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              {t('Vazgeç', 'Cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Durum satırları — gruplardan bağımsız */}
      {isLoading && (
        <div className="py-6 text-[13px] text-neutral-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('Toplantılar yükleniyor…', 'Loading meetings…')}
        </div>
      )}

      {error && (
        <div className="py-4 text-[13px] text-rose-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {!isLoading && !error && meetings.length === 0 && (
        <p className="py-4 text-center text-[13px] text-neutral-500">{t('Henüz kayıtlı toplantı yok.', 'No meetings recorded yet.')}</p>
      )}

      {/* Son bir hafta — her gün kendi tarih başlığıyla, en yeni gün en üstte */}
      {recentGroups.map((group) => (
        <div key={group.key} className="space-y-2">
          <h2 className={`text-[11px] font-bold tracking-[0.09em] uppercase px-0.5 ${
            isDarkMode ? 'text-neutral-500' : 'text-neutral-500'
          }`}>
            {group.heading}
          </h2>

          <div className={`divide-y ${isDarkMode ? 'divide-[#343438]' : 'divide-neutral-100'}`}>
            {group.meetings.map((meeting) =>
              renderRow(
                meeting,
                meeting.time,
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                  meeting.status === 'in-progress' ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]' : 'bg-neutral-600'
                }`}></div>
              )
            )}
          </div>
        </div>
      ))}

      {/* Geçmiş */}
      {earlierMeetings.length > 0 && (
        <div className="space-y-2 pt-2.5">
          <h2 className={`text-[11px] font-bold tracking-[0.09em] uppercase px-0.5 ${
            isDarkMode ? 'text-neutral-500' : 'text-neutral-500'
          }`}>
            {t('Geçmiş Toplantılar', 'Past meetings')}
          </h2>

          <div className={`divide-y ${isDarkMode ? 'divide-[#343438]' : 'divide-neutral-100'}`}>
            {earlierMeetings.map((meeting) =>
              renderRow(
                meeting,
                meeting.date,
                <FileText className="w-4 h-4 text-neutral-500 shrink-0" />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};
